import { Outlet } from '@tanstack/react-router'
import { motion, useDragControls, useReducedMotion } from 'motion/react'
import { useLayoutEffect, useRef, useState } from 'react'
import { useMap } from 'react-map-gl/maplibre'
import { twMerge } from 'tailwind-merge'
import { UI_SPRING } from '@/components/shared/motion/spring.const'
import { playwrightTestId } from '@/components/shared/utils/playwright'
import { mapOverlayHairlineClassName } from '../mapOverlayChrome.const'
import { SheetGrabHandle } from '../mobile/SheetGrabHandle'
import { useInspectorRenderableFeatures } from '../SidebarInspector/useInspectorRenderableFeatures'
import { useCalculatorNeedsArea } from './calculator/useCalculatorNeedsArea'
import { useMeasureNeedsShape } from './measure/useMeasureNeedsShape'
import { modeIdentity } from './modeIdentity'
import { applyModeMapCameraPadding, resetModeMapCameraPadding } from './modeMapCameraPadding'
import { modeMobileDockElevationClassName } from './modePanel.const'
import { useNotesComposeActive } from './notes/useNotesComposeActive'
import { useReviewDrawActive } from './reviewLists/useReviewDrawActive'
import { useOptimisticMode } from './useCurrentMode'

const MODE_MOBILE_DOCK_PEEK = '5.5rem'
const MODE_MOBILE_DOCK_EXPANDED = '62dvh'

/** Required: `mapOverlayBottomRightControlsClassName` reads this var; height is measured via ResizeObserver. */
const setModeMobileDockHeightCssVar = (height: string) =>
  document.documentElement.style.setProperty('--mode-mobile-dock-height', height)

/**
 * Persistent mobile mode panel: a docked bottom sheet without Dialog/backdrop so the map
 * stays pannable and clickable. Collapse is not leave-mode — switching to Karte does that.
 */
export const ModeMobileDock = () => {
  const { mainMap } = useMap()
  const optimisticMode = useOptimisticMode()
  const identity = modeIdentity[optimisticMode]
  const { accent } = identity
  const inspectorOpen = useInspectorRenderableFeatures().length > 0
  const notesComposeActive = useNotesComposeActive()
  const reviewDrawActive = useReviewDrawActive()
  // Summieren and Messen: the map is needed until the first shape exists; then the result opens.
  const calculatorNeedsArea = useCalculatorNeedsArea()
  const measureNeedsShape = useMeasureNeedsShape()
  const composeOrDraw =
    notesComposeActive || reviewDrawActive || calculatorNeedsArea || measureNeedsShape
  const [userExpanded, setUserExpanded] = useState(() => !composeOrDraw)
  const [composeOrDrawSeen, setComposeOrDrawSeen] = useState(composeOrDraw)
  const dragControls = useDragControls()
  const reduceMotion = useReducedMotion()
  const dockRef = useRef<HTMLElement>(null)

  if (composeOrDraw !== composeOrDrawSeen) {
    setComposeOrDrawSeen(composeOrDraw)
    setUserExpanded(!composeOrDraw)
  }

  const expanded = userExpanded && !inspectorOpen
  const height = expanded ? MODE_MOBILE_DOCK_EXPANDED : MODE_MOBILE_DOCK_PEEK

  useLayoutEffect(
    function syncModeMobileDockHeightCssVar() {
      const el = dockRef.current!
      const applyMeasuredHeight = () => {
        const measured = Math.round(el.getBoundingClientRect().height)
        setModeMobileDockHeightCssVar(`${measured}px`)
        applyModeMapCameraPadding(mainMap, measured)
      }
      applyMeasuredHeight()
      const observer = new ResizeObserver(applyMeasuredHeight)
      observer.observe(el)
      return function clearModeMobileDockHeightCssVar() {
        observer.disconnect()
        setModeMobileDockHeightCssVar('0px')
        resetModeMapCameraPadding(mainMap)
      }
    },
    [mainMap],
  )

  const toggleExpanded = () => {
    if (inspectorOpen) return
    setUserExpanded((current) => !current)
  }

  return (
    <motion.section
      ref={dockRef}
      aria-label="Modus-Panel"
      data-testid={playwrightTestId('mode-mobile-dock')}
      data-expanded={expanded ? 'true' : 'false'}
      className={twMerge(
        // White base: the dock lies over the map, and the mode tint alone is translucent.
        'pointer-events-auto fixed inset-x-0 bottom-0 z-20 flex flex-col overflow-hidden rounded-t-xl bg-white pb-[env(safe-area-inset-bottom)]',
        mapOverlayHairlineClassName,
        modeMobileDockElevationClassName,
      )}
      initial={reduceMotion ? false : { y: '100%' }}
      animate={{ y: 0, height }}
      transition={reduceMotion ? { duration: 0 } : UI_SPRING}
      drag="y"
      dragControls={dragControls}
      dragListener={false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0.15, bottom: 0.35 }}
      onDragEnd={(_event, info) => {
        if (inspectorOpen) return
        if (info.offset.y > 80 || info.velocity.y > 500) {
          setUserExpanded(false)
          return
        }
        if (info.offset.y < -80 || info.velocity.y < -500) {
          setUserExpanded(true)
        }
      }}
    >
      <div className={twMerge(accent.className, accent.invertedFgClassName)}>
        <SheetGrabHandle
          onClick={toggleExpanded}
          onPointerDown={(event) => dragControls.start(event)}
          disabled={inspectorOpen}
          ariaExpanded={expanded}
          ariaLabel={expanded ? 'Panel verkleinern' : 'Panel vergrößern'}
          pillClassName="bg-current/35"
          direction={expanded ? 'down' : 'up'}
        />
      </div>
      <div className={twMerge('min-h-0 flex-1 overflow-hidden', accent.tintClassName)}>
        <Outlet />
      </div>
    </motion.section>
  )
}
